/**
 * Operations Dashboard Page
 *
 * Main dashboard showing operations metrics and KPIs.
 */

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, FolderKanban, CheckSquare, AlertTriangle, DollarSign, BarChart3, Table2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MetricCard } from '@/components/MetricCard';
import { formatCompactCurrency } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  useDashboardSummary,
  useWorkload,
  useTimeline,
  useActivity,
  useProjectsKanban,
  metricsKeys,
  kanbanKeys,
} from '../hooks/useOperationsMetrics';
import { TasksOverviewCard } from '../components/TasksOverviewCard';
import { TeamWorkloadCard } from '../components/TeamWorkloadCard';
import { UpcomingDeadlinesCard } from '../components/UpcomingDeadlinesCard';
import { RecentActivityFeed } from '../components/RecentActivityFeed';
import { useKpiScorecard, kpiKeys } from '@/features/kpis/hooks/useKpis';
import { tasksKeys } from '@/features/operations-tasks/hooks/useTasks';
import { financialKeys } from '@/features/operations-financial/hooks/useFinancialMetrics';
import { KpiPeriodCard } from '../components/KpiPeriodCard';
import { TodoThisWeekCard } from '../components/TodoThisWeekCard';
import { ValueVelocityCard } from '../components/ValueVelocityCard';
import { ProjectsTableView } from '../components/ProjectsTableView';
import { QuoteOperationsOverviewCard } from '../components/QuoteOperationsOverviewCard';
import { summarizeQuoteKanban, computeStageBucketSummary } from '../quoteSummary';

/**
 * Summary tiles shown under the metrics row. These bucket the fulfillment
 * STAGES (Approved = New Purchase Order; In Progress = Planification…Invoicing;
 * Invoiced = Customer Onboarding + Done) so the figures reconcile with the
 * Kanban columns — matching the Operations Kanban summary bar. Labels reuse the
 * `quotes` namespace so they read identically to the other summary tiles.
 */
const QUOTE_STATUS_TILES = [
  { status: 'approved', labelKey: 'stats.approved', fallback: 'Approved', color: 'blue' },
  { status: 'inprogress', labelKey: 'stats.inProgress', fallback: 'In Progress', color: 'orange' },
  { status: 'invoiced', labelKey: 'stats.invoiced', fallback: 'Invoiced', color: 'green' },
] as const;

export function OperationsDashboardPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'table'>('dashboard');

  // Fetch all dashboard data
  const { data: summary, isLoading: summaryLoading, isFetching: summaryFetching, isError: summaryError } = useDashboardSummary();
  const { data: quoteKanban, isLoading: quoteKanbanLoading, isFetching: quoteKanbanFetching } = useProjectsKanban({ filter: 'all' });
  const { data: workload, isLoading: workloadLoading, isFetching: workloadFetching, isError: workloadError } = useWorkload();
  const { data: timeline, isLoading: timelineLoading, isFetching: timelineFetching, isError: timelineError } = useTimeline(14);
  const { data: activity, isLoading: activityLoading, isFetching: activityFetching, isError: activityError } = useActivity(20);

  // KPI scorecards (weekly and monthly)
  const { data: weeklyScorecard, isLoading: weeklyLoading, isFetching: weeklyFetching } = useKpiScorecard({ period_type: 'weekly' });
  const { data: monthlyScorecard, isLoading: monthlyLoading, isFetching: monthlyFetching } = useKpiScorecard({ period_type: 'monthly' });

  const { t } = useTranslation('quotes');

  const quoteSummary = summarizeQuoteKanban(quoteKanban);
  const statusSummary = useMemo(
    () => computeStageBucketSummary(quoteKanban),
    [quoteKanban],
  );
  const isRefreshing = summaryFetching || quoteKanbanFetching || workloadFetching || timelineFetching || activityFetching || weeklyFetching || monthlyFetching;

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: metricsKeys.all });
    queryClient.invalidateQueries({ queryKey: kanbanKeys.all });
    queryClient.invalidateQueries({ queryKey: kpiKeys.scorecardAll() });
    queryClient.invalidateQueries({ queryKey: tasksKeys.lists() });
    queryClient.invalidateQueries({ queryKey: financialKeys.all });
  }, [queryClient]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold">Operations Dashboard</h1>
            <p className="text-muted-foreground">
              Overview of projects, tasks, and team performance
            </p>
          </div>
          {/* Tab toggle */}
          <div className="flex items-center border rounded-md" role="tablist" aria-label="View mode">
            <Button
              variant={activeTab === 'dashboard' ? 'secondary' : 'ghost'}
              size="sm"
              role="tab"
              aria-selected={activeTab === 'dashboard'}
              onClick={() => setActiveTab('dashboard')}
            >
              <BarChart3 className="h-4 w-4 mr-1" />
              Dashboard
            </Button>
            <Button
              variant={activeTab === 'table' ? 'secondary' : 'ghost'}
              size="sm"
              role="tab"
              aria-selected={activeTab === 'table'}
              onClick={() => setActiveTab('table')}
            >
              <Table2 className="h-4 w-4 mr-1" />
              Table
            </Button>
          </div>
        </div>
        {activeTab !== 'table' && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        )}
      </div>

      {activeTab === 'dashboard' && (
        <>
          {(summaryError || workloadError || timelineError || activityError) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
              <span className="text-red-700">Some dashboard data failed to load.</span>
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                Retry
              </Button>
            </div>
          )}

          {/* KPI Period Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiPeriodCard
              title="KPI This Week"
              scorecard={weeklyScorecard}
              periodIndex={weeklyScorecard ? weeklyScorecard.periods.length - 1 : 0}
              loading={weeklyLoading}
            />
            <KpiPeriodCard
              title="KPI Last Week"
              scorecard={weeklyScorecard && weeklyScorecard.periods.length >= 2 ? weeklyScorecard : undefined}
              periodIndex={weeklyScorecard ? Math.max(0, weeklyScorecard.periods.length - 2) : 0}
              loading={weeklyLoading}
            />
            <KpiPeriodCard
              title="KPI This Month"
              scorecard={monthlyScorecard}
              periodIndex={monthlyScorecard ? monthlyScorecard.periods.length - 1 : 0}
              loading={monthlyLoading}
            />
            <KpiPeriodCard
              title="KPI Last Month"
              scorecard={monthlyScorecard && monthlyScorecard.periods.length >= 2 ? monthlyScorecard : undefined}
              periodIndex={monthlyScorecard ? Math.max(0, monthlyScorecard.periods.length - 2) : 0}
              loading={monthlyLoading}
            />
          </div>

          {/* Todo & Velocity Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TodoThisWeekCard />
            <ValueVelocityCard />
          </div>

          {/* Summary Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Active Quotes"
              value={quoteSummary.activeCount}
              icon={<FolderKanban />}
              color="blue"
              loading={quoteKanbanLoading}
            />
            <MetricCard
              title="Total Quote Value"
              value={formatCompactCurrency(quoteSummary.totalValue || 0)}
              icon={<DollarSign />}
              color="green"
              loading={quoteKanbanLoading}
            />
            <MetricCard
              title="Open Tasks"
              value={summary ? summary.tasks.total - summary.tasks.completed : undefined}
              icon={<CheckSquare />}
              color="purple"
              loading={summaryLoading}
            />
            <MetricCard
              title="Overdue Tasks"
              value={summary?.tasks.overdue}
              icon={<AlertTriangle />}
              color={summary?.tasks.overdue && summary.tasks.overdue > 0 ? 'red' : 'orange'}
              loading={summaryLoading}
            />
          </div>

          {/* Quote Status Summary Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {QUOTE_STATUS_TILES.map((tile) => {
              const entry = statusSummary.find((s) => s.status === tile.status);
              return (
                <MetricCard
                  key={tile.status}
                  title={t(tile.labelKey, tile.fallback)}
                  value={entry?.count ?? 0}
                  suffix={formatCompactCurrency(entry?.amount ?? 0)}
                  color={tile.color}
                  loading={quoteKanbanLoading}
                />
              );
            })}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <QuoteOperationsOverviewCard
              data={quoteSummary}
              loading={quoteKanbanLoading}
            />
            <TasksOverviewCard
              data={summary?.tasks}
              loading={summaryLoading}
            />
          </div>

          {/* Data Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TeamWorkloadCard
              data={workload}
              loading={workloadLoading}
            />
            <UpcomingDeadlinesCard
              data={timeline}
              loading={timelineLoading}
            />
          </div>

          {/* Activity Feed */}
          <RecentActivityFeed
            data={activity}
            loading={activityLoading}
          />
        </>
      )}
      {activeTab === 'table' && <ProjectsTableView />}
    </div>
  );
}
